<template>
  <SloLoader :loading="shippingAddressesLoading" size="xl">
    <article class="personal-details--address-book">
      <div class="p-md sm:p-lg">
        <h3 class="text-xl fw-strong">
          {{ 'Saved Addresses' | t }}
        </h3>
      </div>
      <div class="px-md sm:px-lg">
        <transition name="fade-up">
          <SloModal
            v-if="showRemoveAddressModal"
            :max-width="500"
            :form="false"
            @dismiss="handleCancelRemoveAddress"
          >
            <template v-slot:title>{{ 'Remove Address' | t }}</template>
            <p>{{ 'Are you sure you want to remove this address?' | t }}</p>
            <template v-slot:footer>
              <SloButton
                :disabled="shippingAddressesLoading"
                @click="handleRemoveAddress"
                fill="outline"
              >
                <SloLoader :loading="shippingAddressesLoading" size="md">
                  {{ 'Yes, remove this address' | t }}
                </SloLoader>
              </SloButton>
              <SloButton
                :disabled="shippingAddressesLoading"
                @click="handleCancelRemoveAddress"
              >
                {{ 'No, keep this address' | t }}
              </SloButton>
            </template>
          </SloModal>
        </transition>
        <ShippingAddresses
          :show-modify-controls="true"
          :shipping-addresses="shippingAddresses"
          :shipping-address-id="shippingAddressId"
          @removeAddress="openRemoveAddressModal"
          @updateAddress="handleUpdateAddress"
          @makeAddressDefault="handleMakeAddressDefault"
        />
      </div>
      <div class="personal-details--footer flex justify-end px-md py-md bg-gray-100 sm:px-lg">
        <SloButton
          fill="solid"
          :to="{name: 'marketplace-account-address-add'}"
        >
          {{ 'Add New Address' | t }}
        </SloButton>
      </div>
    </article>
    <div class="mt-lg">
      <p class="fw-bold">{{ 'Currently shipping to US only.' | t }}</p>
    </div>
  </SloLoader>
</template>

<script>
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import ShippingAddresses from '../components/ShippingAddresses.vue';

export default {
  components: {
    SloLoader,
    SloButton,
    SloModal,
    ShippingAddresses
  },
  filters: {
    t
  },
  data() {
    return {
      shippingAddressesLoading: false,
      shippingAddresses: [],
      shippingAddressId: null,
      showRemoveAddressModal: false,
      shippingAddressToRemove: null
    };
  },
  async mounted() {
    await this.getShippingAddresses();
  },
  methods: {
    async getShippingAddresses() {
      this.shippingAddressesLoading = true;
      await marketplaceApi.getCurrentUserAddresses()
        .then(async res => {
          this.shippingAddressesLoading = false;
          this.shippingAddresses = res.data.addresses;
          if (this.shippingAddresses.length === 0) {
            await this.$router.push({name: 'marketplace-account-address-add'});
          }
          this.shippingAddressId = this.shippingAddresses.find(address => address.defaultAddress)?.id;
        })
        .catch(() => {
          this.shippingAddressesLoading = false;
          this.$snotify.error(t('Error loading your addresses, please try again'));
        });
    },
    async handleRemoveAddress() {
      this.shippingAddressesLoading = true;
      await marketplaceApi.removeCurrentUserAddress(this.shippingAddressToRemove)
        .then(async() => {
          this.shippingAddressToRemove = null;
          this.showRemoveAddressModal = false;
          await this.getShippingAddresses();
          this.shippingAddressesLoading = false;
          this.showRemoveAddressModal = false;
          this.$snotify.clear();
          this.$snotify.success(t('Address removed'));
        })
        .catch(() => {
          this.shippingAddressToRemove = null;
          this.showRemoveAddressModal = false;
          this.shippingAddressesLoading = false;
          this.$snotify.error(t('Error occurred while removing the address'));
        });
    },
    async handleMakeAddressDefault(address) {
      this.shippingAddressesLoading = true;
      await marketplaceApi.updateCurrentUserAddress({...address, defaultAddress: true})
        .then(async() => {
          await this.getShippingAddresses();
          this.shippingAddressesLoading = false;
          this.$snotify.clear();
          this.$snotify.success(t('Default address updated'));
        })
        .catch(() => {
          this.shippingAddressesLoading = false;
          this.$snotify.error(t('Error occurred while making address default'));
        });
    },
    async handleUpdateAddress(addressId) {
      await this.$router.push({
        name: 'marketplace-account-address-update',
        params: {id: addressId}
      });
    },
    handleCancelRemoveAddress() {
      this.showRemoveAddressModal = false;
      this.shippingAddressToRemove = null;
    },
    openRemoveAddressModal(addressId) {
      this.showRemoveAddressModal = true;
      this.shippingAddressToRemove = addressId;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.personal-details--footer {
  border-radius: 0 0 1.2rem 1.2rem;
}

:deep(.marketplace-checkout--shipping-addresses) {
  grid-template-columns: 1fr;

  @media (min-width: $screen-md) {
    grid-template-columns: repeat(2, 1fr);
  }

  input {
    display: none;
    position: static;
    inset: initial;
    cursor: initial;
  }
}
</style>
